import type Order from '~/types/Order';
import type Rate from '~/types/Rate';
import type Shipment from '~/types/Shipment';
import type Transaction from '~/types/Transaction';

import { type APIError } from '~/types/APIError';
import { getAddresses, isAddressIncomplete } from '~/types/Address';
import { getCustomsPhoneErrors } from '~/types/CustomsDeclaration';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import { TransactionStatus } from '~/types/Transaction';

const hasRatingErrors = (
  order: Order,
  batchEntry: Maybe<BatchEntryExtended>,
  shipment: Maybe<Shipment>,
  transaction: Maybe<Transaction>,
  rates: Rate[],
  hasValidSenderAddress: boolean,
): {
  hasErrors: boolean;
  hasExceededPackageWeightError: boolean;
  hasParcelWeightErrors: boolean;
  hasPhoneNumberRequiredError: boolean;
  hasShipmentErrors: boolean;
  showParcelError: boolean;
  showRecipientAddressError: boolean;
  showSenderAddressError: boolean;
  transactionHasErrors: boolean;
} => {
  const showRecipientAddressError = isAddressIncomplete(order?.toAddress) && !batchEntry?.addressTo;
  const showParcelError = !batchEntry?.parcel;
  const { addressFrom, addressTo } = getAddresses(order, batchEntry);
  const { hasPhoneNumberRequiredError } = getCustomsPhoneErrors({ addressFrom, addressTo });
  const isAddressFromIncomplete = !!addressFrom && isAddressIncomplete(addressFrom);
  const showSenderAddressError =
    (!hasValidSenderAddress && !addressFrom) || isAddressFromIncomplete;

  const transactionHasErrors =
    (batchEntry?.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION &&
      transaction?.status === TransactionStatus.ERROR) ||
    batchEntry?.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED;

  const hasShipmentErrors = !!shipment && shipment.messages.length > 0 && rates.length === 0;

  const hasParcelWeightErrors = batchEntry
    ? !(Number(batchEntry.parcel?.weight) > 0 && batchEntry.parcel?.weightUnit)
    : !(Number(order?.parcel?.weight) > 0 && order?.parcel?.weightUnit);

  const batchEntryErrors = batchEntry?.errors ? [...batchEntry.errors] : [];
  const orderErrors =
    batchEntry?.partialFulfillmentRank === 0 || order.partialFulfillmentRequests.length === 0
      ? [...order.errors]
      : [];
  const errors: APIError[] = batchEntryErrors.length ? batchEntryErrors : orderErrors;
  const hasExceededPackageWeightError = !!errors.find(
    (error) => error.code === 'order_weight_exceeded',
  );

  const hasErrors =
    showRecipientAddressError ||
    showParcelError ||
    showSenderAddressError ||
    transactionHasErrors ||
    hasShipmentErrors ||
    hasParcelWeightErrors ||
    hasExceededPackageWeightError ||
    hasPhoneNumberRequiredError;

  return {
    hasErrors,
    hasExceededPackageWeightError,
    hasParcelWeightErrors,
    hasPhoneNumberRequiredError,
    hasShipmentErrors,
    showParcelError,
    showRecipientAddressError,
    showSenderAddressError,
    transactionHasErrors,
  };
};

export default hasRatingErrors;
